import type { AuditListItemExtended, RiskLevel } from "@/types";

export type DashboardRiskTone = "high" | "med" | "low";

export interface DashboardMetrics {
  auditsThisMonth: number;
  auditsLastMonth: number;
  openHighRisk: number;
  complianceScore: number | null;
  complianceTone: DashboardRiskTone;
  recentAudits: AuditListItemExtended[];
}

function isInMonth(iso: string, year: number, month: number): boolean {
  const date = new Date(iso);
  return date.getFullYear() === year && date.getMonth() === month;
}

export function auditRiskTagLevel(
  level: RiskLevel | null | undefined,
): DashboardRiskTone | null {
  if (!level) return null;
  if (level === "RED") return "high";
  if (level === "YELLOW") return "med";
  return "low";
}

export function complianceToneFromScore(
  score: number | null,
): DashboardRiskTone {
  if (score === null) return "med";
  if (score >= 70) return "low";
  if (score >= 40) return "med";
  return "high";
}

export function complianceRingColor(tone: DashboardRiskTone): string {
  if (tone === "low") return "var(--risk-low)";
  if (tone === "med") return "var(--risk-med)";
  return "var(--risk-high)";
}

/**
 * Derive dashboard KPIs from a recent audit-list page. No aggregate backend —
 * the thin dashboard path reuses GET /audits (see pirx-3apc9 epic notes).
 */
export function computeDashboardMetrics(
  audits: AuditListItemExtended[],
  options: { recentLimit?: number; now?: Date } = {},
): DashboardMetrics {
  const now = options.now ?? new Date();
  const recentLimit = options.recentLimit ?? 8;
  const thisYear = now.getFullYear();
  const thisMonth = now.getMonth();
  const lastMonthDate = new Date(thisYear, thisMonth - 1, 1);
  const lastMonthYear = lastMonthDate.getFullYear();
  const lastMonth = lastMonthDate.getMonth();

  let auditsThisMonth = 0;
  let auditsLastMonth = 0;
  let openHighRisk = 0;
  const scores: number[] = [];

  for (const audit of audits) {
    if (isInMonth(audit.createdAt, thisYear, thisMonth)) {
      auditsThisMonth += 1;
    }
    if (isInMonth(audit.createdAt, lastMonthYear, lastMonth)) {
      auditsLastMonth += 1;
    }

    if (audit.status === "COMPLETED") {
      if (audit.overallRiskLevel === "RED") {
        openHighRisk += 1;
      }
      if (audit.evidenceSufficiencyScore != null) {
        scores.push(audit.evidenceSufficiencyScore);
      }
    }
  }

  const complianceScore = scores.length > 0
    ? Math.round(scores.reduce((sum, value) => sum + value, 0) / scores.length)
    : null;

  return {
    auditsThisMonth,
    auditsLastMonth,
    openHighRisk,
    complianceScore,
    complianceTone: complianceToneFromScore(complianceScore),
    recentAudits: audits.slice(0, recentLimit),
  };
}
